import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ROLES, type CommitLedgerEntry, type EnterpriseContractInput, type FeatureGrant, type Meter, type OrgSettings, type QuotaCredit, type Role, type SdkLimits, type StaffOrgDetail, type SupportAccessGrant } from "@backstory/protocol";
import { ROLE_LABELS, ROLE_SUMMARY } from "../../auth/roles";
import { BusyButton, ConfirmDialog, DataTable, Dialog, Field, Meter as MeterBar, RowMenu, SearchSelect, Switch, fmtNum, fmtUsd, useToast } from "../../components/ui";
import { SaveBar } from "../../components/UnsavedChanges";
import { formatAccessLength, formatDateTime, formatRelative } from "../../lib/format";
import { formatPhone } from "../../lib/phone";
import { addressLines } from "../../routes/org/OrgProfileFields";
import { normalizeNetwork } from "../../routes/org/NetworksPage";
import { limitNotices, meterLimitStatus, meterStopped, recordingInstruction } from "../billing/limits";
import { staffApi, staffKeys } from "./api";
import { ACTIVITY_FAMILIES, ActivityFeed, METERS, PlanBadge, BillingStatusBadge, featureLabel, fmtUnits, formatDate, meterNoun, meterOf, planLabel, useDebounced } from "./customers";

export type CustomerTab = "overview" | "usage" | "billing" | "members" | "credits" | "access" | "networks" | "activity" | "settings";
export const TABS: Array<{ id: CustomerTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "usage", label: "Usage" },
  { id: "billing", label: "Billing" },
  { id: "members", label: "People" },
  { id: "credits", label: "Grants" },
  { id: "access", label: "Support access" },
  { id: "networks", label: "Networks" },
  { id: "activity", label: "Activity" },
  { id: "settings", label: "Settings" },
];

/** The record's limits in the shape the billing helpers read. */
export function sdkLimitsOf(d: StaffOrgDetail): SdkLimits {
  const l = d.limits;
  const reason = (r?: string) => (r ? (r as SdkLimits["sessionsReason"]) : undefined);
  return {
    sessions: l.sessions,
    sessionsReason: reason(l.sessionsReason),
    canvas: { allowed: !l.canvasOff, reason: reason(l.canvasReason) },
    live: { allowed: !l.liveOff, reason: reason(l.liveReason) },
    remoteControl: { allowed: !d.org.settings.liveNoRemoteControl },
    supportCalling: { allowed: !d.org.settings.supportCallingDisabled },
    customEvents: { allowed: !l.customEventsOff, reason: reason(l.customEventsReason) },
    webrtc: { allowed: !l.webrtcOff, reason: reason(l.webrtcReason) },
    recheckSec: 900,
  };
}

export function useRefreshCustomer(orgId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: staffKeys.org(orgId) });
    void qc.invalidateQueries({ queryKey: ["staff", "orgs"] });
    void qc.invalidateQueries({ queryKey: ["staff", "activity"] });
  };
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export function OverviewTab({ d }: { d: StaffOrgDetail }) {
  const p = d.org.profile ?? {};
  const notices = limitNotices(sdkLimitsOf(d));
  const aiBalance = d.balances.ai_credits_usd ?? 0;
  const sessions = d.usage.meters.stored_sessions;
  const up = d.customer.aiFeedbackUp ?? 0;
  const down = d.customer.aiFeedbackDown ?? 0;
  return (
    <div className="cust-overview">
      {notices.map((n) => (
        <div key={n.text} className={`banner ${n.tone}`} role="status">
          {n.text}
        </div>
      ))}
      <div className="cust-stats cust-tiles">
        <Tile label="Plan" value={planLabel(d.plan)} hint={planHint(d)} tone={d.billing.status === "past_due" || d.billing.status === "unpaid" ? "warn" : undefined} />
        <Tile label="Sessions this month" value={fmtNum(Math.round(sessions?.used ?? 0))} hint={sessions ? `${fmtNum(sessions.included)} included` : undefined} />
        <Tile label="AI credit balance" value={fmtUsd(aiBalance)} hint="Granted on top of the plan" />
        <Tile label="People" value={fmtNum(d.members.length)} hint={d.invitations.length ? `${d.invitations.length} invited` : undefined} />
        <Tile label="Apps" value={fmtNum(d.projects.length)} />
        <Tile label="AI feedback" hint={up + down === 0 ? "No ratings yet" : undefined}>
          {up + down > 0 && (
            <div className="cust-votes">
              <span className="up" aria-label={`${fmtNum(up)} helpful`}>
                <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2.5 14 13H2z" fill="currentColor" /></svg>
                {fmtNum(up)}
              </span>
              <span className="down" aria-label={`${fmtNum(down)} not helpful`}>
                <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 13.5 2 3h12z" fill="currentColor" /></svg>
                {fmtNum(down)}
              </span>
            </div>
          )}
          {up + down === 0 && <div className="value">0</div>}
        </Tile>
        <Tile label="Last activity" value={d.customer.lastActivityAt ? formatRelative(d.customer.lastActivityAt) : "None yet"} hint={`Customer since ${formatDate(d.org.createdAt)}`} />
        <Tile
          label="Invoiced"
          value={d.money ? fmtUsd(d.money.invoicedUsd) : "—"}
        />
        <Tile
          label="Model cost"
          value={d.money ? fmtUsd(d.money.modelCostUsd) : "—"}
        />
        <Tile
          label="Margin"
          value={d.money ? fmtUsd(d.money.marginUsd) : "—"}
          tone={d.money && d.money.marginUsd < 0 ? "warn" : undefined}
          hint="Invoiced minus model calls, stored sessions, and custom events"
        />
      </div>

      <div className="cust-grid">
        <section className="card cust-card">
          <div className="cust-card-head">
            <h3>Organization</h3>
          </div>
          <dl className="cust-kv">
            <dt>Email</dt>
            <dd>{p.contactEmail ? <a href={`mailto:${p.contactEmail}`}>{p.contactEmail}</a> : <span className="muted">Not set</span>}</dd>
            <dt>Phone</dt>
            <dd>{p.phone ? <a href={`tel:${p.phone}`}>{formatPhone(p.phone)}</a> : <span className="muted">Not set</span>}</dd>
            <dt>Website</dt>
            <dd>
              {p.website ? (
                <a href={/^https?:\/\//.test(p.website) ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer">
                  {p.website}
                </a>
              ) : (
                <span className="muted">Not set</span>
              )}
            </dd>
            <dt>Address</dt>
            <dd>
              {addressLines(p).length ? (
                <address>
                  {addressLines(p).map((l) => (
                    <div key={l}>{l}</div>
                  ))}
                </address>
              ) : (
                <span className="muted">Not set</span>
              )}
            </dd>
            <dt>Time zone</dt>
            <dd>{p.timezone || <span className="muted">Not set</span>}</dd>
          </dl>
        </section>

        <section className="card cust-card">
          <div className="cust-card-head">
            <h3>Apps</h3>
          </div>
          {d.projects.length === 0 ? (
            <p className="muted small">No apps yet.</p>
          ) : (
            <ul className="cust-list">
              {d.projects.map((pr) => {
                const rec = recordingInstruction(pr.recording, d.limits);
                return (
                  <li key={pr.id} className="cust-app">
                    <div className="cust-app-head">
                      <strong>{pr.name}</strong>
                      <span className={`badge ${rec.tone}`} title={rec.narrowedBy ? `Saved setting narrowed: ${rec.narrowedBy}` : undefined}>{rec.label}</span>
                    </div>
                    <span className="muted small">Kept {pr.retentionDays} days · added {formatDate(pr.createdAt)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

      </div>
    </div>
  );
}

/** A status line only when it says more than the plan name. Free with no subscription is just Free. */
function planHint(d: StaffOrgDetail): string | undefined {
  const s = d.billing.status;
  if (s === "trialing") return d.billing.trialEndsAt ? `Trial ends ${formatDate(d.billing.trialEndsAt)}` : "Trial";
  if (s === "past_due") return "Past due";
  if (s === "unpaid") return "Unpaid";
  if (s === "canceled") return "Canceled";
  if (s === "none" && d.plan !== "free") return "Not billed";
  if (s === "active" && d.billing.currentPeriodEnd) return `Renews ${formatDate(d.billing.currentPeriodEnd)}`;
  return undefined;
}

function Tile({ label, value, hint, tone, children }: { label: string; value?: string; hint?: string; tone?: string; children?: ReactNode }) {
  return (
    <div className={`card cust-stat${tone ? ` ${tone}` : ""}`}>
      <div className="label">{label}</div>
      {value != null && <div className="value">{value}</div>}
      {children}
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage
// ---------------------------------------------------------------------------

export function UsageTab({ d }: { d: StaffOrgDetail }) {
  const limits = sdkLimitsOf(d);
  return (
    <div className="cust-usage">
      <p className="muted small" style={{ marginTop: 0 }}>
        {d.usage.month} · what the plan includes plus any credits, against what has been used. Credits raise the included amount.
      </p>
      <div className="meters">
        {METERS.map((m) => {
          const v = d.usage.meters[m.id];
          if (!v) return null;
          return (
            <div key={m.id} className="cust-meter">
              <MeterBar
                label={m.label}
                used={v.used}
                included={v.included}
                projected={meterStopped(m.id, limits) ? undefined : v.projectedMonthEnd}
                unit={m.unit}
                format={m.usd ? fmtUsd : undefined}
                overageUnits={v.overageUnits}
                overageUsd={v.overageUsd}
                overageUnitPrice={m.usd ? undefined : v.overageUnitPrice}
                overagePer1000={m.id === "stored_sessions"}
                overagePerMillion={m.perMillion}
                status={meterLimitStatus(m.id, limits)}
              />
            </div>
          );
        })}
      </div>
      {d.usage.customEvents && (d.usage.customEvents.projects.length > 0 || d.usage.customEvents.names.length > 0) && (
        <section className="card cust-card" style={{ marginTop: 16 }}>
          <div className="cust-card-head"><h3>Custom events this month</h3></div>
          <div className="form-grid">
            <div>
              <h4>By key</h4>
              <ul>
                {d.usage.customEvents.projects.map((p) => (
                  <li key={p.projectId}>{p.name || "Untitled"} · {fmtNum(p.used)}{p.cap ? ` of ${fmtNum(p.cap)}` : ""}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4>By name</h4>
              <ul>
                {d.usage.customEvents.names.map((n) => (
                  <li key={n.name}>{n.name} · {fmtNum(n.used)}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
      <section className="card cust-card" style={{ marginTop: 16 }}>
        <div className="cust-card-head">
          <h3>Caps the customer set</h3>
        </div>
        <dl className="cust-kv">
          {METERS.map((m) => (
            <div key={m.id} className="cust-kv-row">
              <dt>{m.label}</dt>
              <dd>{d.usage.caps.hardCap[m.id] != null ? fmtUnits(m.id, d.usage.caps.hardCap[m.id]!, false) : <span className="muted">No cap</span>}</dd>
            </div>
          ))}
          <div className="cust-kv-row">
            <dt>When the session cap is reached</dt>
            <dd>{d.usage.caps.onSessionCap === "stop" ? "Stop recording" : "Record errors and triggers only"}</dd>
          </div>
          <div className="cust-kv-row">
            <dt>Custom events past the included amount</dt>
            <dd>{d.usage.caps.customEventOverageAllowed ? "Charged" : "Stops at the included amount"}</dd>
          </div>
          <div className="cust-kv-row">
            <dt>AI past the included credits</dt>
            <dd>{d.usage.caps.deepAnalysisOverageAllowed ? `Allowed${d.usage.caps.deepAnalysisSpendCapUsd ? ` up to ${fmtUsd(d.usage.caps.deepAnalysisSpendCapUsd)}` : ""}` : "Pauses at zero"}</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

const OFFER_STATUS: Record<string, string> = {
  sent: "Sent",
  awaiting_payment: "Waiting for payment",
  active: "Paid",
  declined: "Declined",
  expired: "Expired",
};

export function BillingTab({ d }: { d: StaffOrgDetail }) {
  const offer = d.enterpriseOffer;
  const openOffer = offer && offer.status !== "expired" && offer.status !== "declined" && offer.status !== "active";
  return (
    <div className="cust-billing">
      {d.billing.testMode && (
        <div className="banner warn" role="status" style={{ marginBottom: 16 }}>
          These charges are test charges. Cards are not billed.
        </div>
      )}
      <section className="card cust-card">
        <div className="cust-card-head">
          <div>
            <h3>Plan</h3>
            <p className="muted small" style={{ margin: 0 }}>{planHint(d) ?? "Self-serve plans change when the customer pays."}</p>
          </div>
          <span className="cust-plan">
            <PlanBadge plan={d.plan} />
            {(d.billing.status !== "none" || d.plan !== "free") && <BillingStatusBadge status={d.billing.status} />}
          </span>
        </div>
        <dl className="cust-kv">
          <div className="cust-kv-row"><dt>Payment method</dt><dd>{d.billing.paymentMethodSet ? "Card on file" : <span className="muted">None</span>}</dd></div>
          {d.billing.trialEndsAt && <div className="cust-kv-row"><dt>Trial ends</dt><dd>{formatDate(d.billing.trialEndsAt)}</dd></div>}
          {d.billing.currentPeriodEnd && <div className="cust-kv-row"><dt>Current period ends</dt><dd>{formatDate(d.billing.currentPeriodEnd)}</dd></div>}
        </dl>
      </section>

      <section className="card cust-card" style={{ marginTop: 16 }}>
        <div className="cust-card-head">
          <div>
            <h3>Enterprise</h3>
            <p className="muted small" style={{ margin: 0 }}>A custom agreement the customer reviews and pays before their plan changes.</p>
          </div>
          {!d.enterpriseContract && !openOffer && (
            <Link className="btn primary" to="/staff/orgs/$org/enterprise" params={{ org: d.orgId }}>Create offer</Link>
          )}
        </div>
        {d.enterpriseContract ? (
          <dl className="cust-kv">
            <div className="cust-kv-row"><dt>Annual commitment</dt><dd>{fmtUsd(d.enterpriseContract.annualCommitUsd)}</dd></div>
            <div className="cust-kv-row"><dt>Monthly platform fee</dt><dd>{fmtUsd(d.enterpriseContract.monthlyPlatformUsd)}</dd></div>
            <div className="cust-kv-row"><dt>Term</dt><dd>{formatDate(d.enterpriseContract.periodStart)} to {formatDate(d.enterpriseContract.periodEnd)}</dd></div>
            <div className="cust-kv-row"><dt>Billing contact</dt><dd>{d.enterpriseContract.contactEmail}</dd></div>
            <div className="cust-kv-row"><dt>Target margin</dt><dd>{d.enterpriseContract.targetMarginPct}%</dd></div>
            <div className="cust-kv-row"><dt>Estimated monthly cost</dt><dd>{fmtUsd(d.enterpriseContract.estimatedCostUsd)}</dd></div>
            <div className="cust-kv-row"><dt>Gross margin on the platform fee</dt><dd>{fmtPct(d.enterpriseContract.monthlyPlatformUsd > 0 ? ((d.enterpriseContract.monthlyPlatformUsd - d.enterpriseContract.estimatedCostUsd) / d.enterpriseContract.monthlyPlatformUsd) * 100 : 0)}</dd></div>
            <div className="cust-kv-row"><dt>Custom events</dt><dd>{fmtNum(d.enterpriseContract.customEventsIncluded)} included · {fmtUsd(d.enterpriseContract.customEventOveragePerMillion)} per extra million</dd></div>
          </dl>
        ) : offer ? (
          <dl className="cust-kv">
            <div className="cust-kv-row"><dt>Status</dt><dd>{OFFER_STATUS[offer.status] ?? offer.status}</dd></div>
            <div className="cust-kv-row"><dt>Sent to</dt><dd>{offer.recipientEmail}</dd></div>
            <div className="cust-kv-row"><dt>Annual commitment</dt><dd>{fmtUsd(offer.annualCommitUsd)}</dd></div>
            <div className="cust-kv-row"><dt>Expires</dt><dd>{formatDate(offer.expiresAt)}</dd></div>
          </dl>
        ) : (
          <p className="muted small">No Enterprise offer has been sent.</p>
        )}
      </section>
      {(d.billing.commit || (d.commitLedger ?? []).length > 0) && <CommitLedgerCard d={d} />}
    </div>
  );
}

const LEDGER_KIND: Record<string, string> = {
  commit: "Commit",
  drawdown: "Month drawn",
  true_up: "Monthly true-up",
  adjustment: "Adjustment",
};

/** What happened to a true-up's bill. Other rows have nothing to bill. */
function ledgerInvoice(e: CommitLedgerEntry): ReactNode {
  if (e.kind !== "true_up") return <span className="muted">—</span>;
  if (e.invoiceId) {
    const label = e.invoiceNumber || "Invoice";
    return (
      <span className="cust-plan">
        <span className={`badge ${e.invoiceStatus === "paid" ? "ok" : "info"}`}>{e.invoiceStatus === "paid" ? "Paid" : "Sent"}</span>
        {e.invoiceUrl ? (
          <a href={e.invoiceUrl} target="_blank" rel="noreferrer">
            {label}
          </a>
        ) : (
          <span>{label}</span>
        )}
        {e.invoicedAt && <span className="muted small">{formatDate(e.invoicedAt)}</span>}
      </span>
    );
  }
  return (
    <span className="cust-plan">
      <span className="badge warn">Not sent</span>
      <span className="muted small">{e.invoiceError === "organization has no billing customer" ? "No card or billing account on file. Retried each day." : e.invoiceError || "Retried each day."}</span>
    </span>
  );
}

function CommitLedgerCard({ d }: { d: StaffOrgDetail }) {
  const c = d.billing.commit;
  return (
    <section className="card cust-card" style={{ marginTop: 16 }}>
      <div className="cust-card-head">
        <div>
          <h3>Commit</h3>
          <p className="muted small" style={{ margin: 0 }}>
            {c ? `${fmtUsd(c.drawnUsd)} of ${fmtUsd(c.totalUsd)} drawn · ${formatDate(c.startsAt)} to ${formatDate(c.endsAt)}` : "No commit on file"}. Usage past the commit is invoiced monthly and due in 7 days.
          </p>
        </div>
      </div>
      <div className="stack-table cust-commit">
        <DataTable
          ariaLabel="Commit ledger"
          rows={d.commitLedger ?? []}
          rowKey={(e) => e.id}
          empty="Nothing has been drawn against the commit yet."
          columns={[
            { key: "when", header: "When", label: "When", className: "nowrap", render: (e) => <span title={formatDateTime(e.at)}>{formatDate(e.at)}</span> },
            { key: "kind", header: "Entry", label: "Entry", render: (e) => LEDGER_KIND[e.kind] ?? e.kind },
            {
              key: "period",
              header: "Period",
              label: "Period",
              render: (e) => (e.periodStart && e.periodEnd ? `${formatDate(e.periodStart)} to ${formatDate(e.periodEnd)}` : e.periodEnd ? `Through ${formatDate(e.periodEnd)}` : <span className="muted">—</span>),
            },
            {
              key: "amount",
              header: "Amount",
              label: "Amount",
              align: "right",
              render: (e) => <span className={`cust-units ${e.amountUsd < 0 ? "neg" : "pos"}`}>{`${e.amountUsd < 0 ? "−" : "+"}${fmtUsd(Math.abs(e.amountUsd))}`}</span>,
            },
            { key: "invoice", header: "Bill", label: "Bill", render: ledgerInvoice },
          ]}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export function MembersTab({ d, onInvite }: { d: StaffOrgDetail; onInvite: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const [removing, setRemoving] = useState<{ userId: string; email: string } | null>(null);
  const [revoking, setRevoking] = useState<{ id: string; email: string } | null>(null);
  const owners = d.members.filter((m) => m.role === "owner").length;

  const setRole = useMutation({
    mutationFn: (v: { userId: string; role: Role }) => staffApi.setOrgMemberRole(d.orgId, v.userId, v.role),
    onSuccess: (_, v) => {
      refresh();
      toast.push("success", `Role changed to ${ROLE_LABELS[v.role]}.`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => staffApi.removeOrgMember(d.orgId, userId),
    onSuccess: () => {
      setRemoving(null);
      refresh();
      toast.push("info", "Member removed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => staffApi.revokeOrgInvitation(d.orgId, id),
    onSuccess: () => {
      setRevoking(null);
      refresh();
      toast.push("info", "Invitation revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <div>
      <div className="cust-section-head">
        <h3>Members</h3>
        <button type="button" className="btn primary" onClick={onInvite}>
          Invite member
        </button>
      </div>
      <div className="card stack-table cust-members">
        <DataTable
          ariaLabel="Members"
          rows={d.members}
          rowKey={(m) => m.userId}
          empty="No members."
          columns={[
            {
              key: "who",
              header: "Member",
              label: "Member",
              render: (m) => (
                <span>
                  <strong>{m.name || m.email}</strong>
                  {m.name && <div className="muted small">{m.email}</div>}
                </span>
              ),
            },
            {
              key: "role",
              header: "Role",
              label: "Role",
              render: (m) => (
                <select
                  className="input"
                  aria-label={`Role for ${m.email}`}
                  value={m.role}
                  title={ROLE_SUMMARY[m.role]}
                  disabled={(m.role === "owner" && owners === 1) || m.source === "scim" || setRole.isPending}
                  onChange={(e) => setRole.mutate({ userId: m.userId, role: e.target.value as Role })}
                  style={{ minWidth: 140 }}
                  autoComplete="off"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              ),
            },
            { key: "mfa", header: "2FA", label: "2FA", render: (m) => (m.mfaEnabled ? <span className="badge ok">On</span> : <span className="badge warn">Off</span>) },
            { key: "src", header: "Source", label: "Source", render: (m) => <span className="badge neutral">{m.source === "scim" ? "Directory" : m.source === "sso" ? "Single sign-on" : "Account"}</span> },
            { key: "last", header: "Last sign-in", label: "Last sign-in", render: (m) => (m.lastLoginAt ? formatRelative(m.lastLoginAt) : <span className="muted">Never</span>) },
            {
              key: "act",
              header: "",
              align: "right",
              render: (m) =>
                (m.role === "owner" && owners === 1) || m.source === "scim" ? null : (
                  <RowMenu label={`Actions for ${m.email}`}>
                    <button type="button" role="menuitem" className="danger" onClick={() => setRemoving({ userId: m.userId, email: m.email })}>
                      Remove member
                    </button>
                  </RowMenu>
                ),
            },
          ]}
        />
      </div>

      <div className="cust-section-head" style={{ marginTop: 24 }}>
        <h3>Pending invitations</h3>
      </div>
      <div className="card stack-table">
        <DataTable
          ariaLabel="Invitations"
          rows={d.invitations}
          rowKey={(i) => i.id}
          empty="No pending invitations."
          columns={[
            { key: "email", header: "Email", label: "Email", render: (i) => i.email },
            { key: "role", header: "Role", label: "Role", render: (i) => ROLE_LABELS[i.role] },
            { key: "exp", header: "Expires", label: "Expires", render: (i) => formatDateTime(i.expiresAt) },
            {
              key: "act",
              header: "",
              align: "right",
              render: (i) => (
                <RowMenu label={`Actions for ${i.email}`}>
                  <button type="button" role="menuitem" className="danger" onClick={() => setRevoking({ id: i.id, email: i.email })}>
                    Revoke invitation
                  </button>
                </RowMenu>
              ),
            },
          ]}
        />
      </div>

      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove member"
        confirmLabel="Remove member"
        busy={remove.isPending}
        onClose={() => !remove.isPending && setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.userId)}
      >
        <p style={{ margin: 0 }}>
          <strong>{removing?.email}</strong> loses access to {d.org.name} immediately.
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!revoking}
        danger
        title="Revoke invitation"
        confirmLabel="Revoke invitation"
        busy={revoke.isPending}
        onClose={() => !revoke.isPending && setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      >
        <p style={{ margin: 0 }}>
          The invitation to <strong>{revoking?.email}</strong> can no longer be accepted.
        </p>
      </ConfirmDialog>
    </div>
  );
}

export function InviteDialog({ d, open, onClose }: { d: StaffOrgDetail; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("developer");
  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("developer");
    }
  }, [open]);
  const invite = useMutation({
    mutationFn: () => staffApi.inviteOrgMember(d.orgId, { email: email.trim(), role }),
    onSuccess: () => {
      refresh();
      toast.push("success", `Invitation sent to ${email.trim()}.`);
      onClose();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const valid = /.+@.+\..+/.test(email.trim());
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite a member"
      subtitle={`They join ${d.org.name} with the role you choose.`}
      width={480}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </button>
          <BusyButton className="btn primary" busy={invite.isPending} busyLabel="Sending…" disabled={!valid} onClick={() => invite.mutate()}>
            Send invitation
          </BusyButton>
        </>
      }
    >
      <Field label="Email">{(p) => <input {...p} className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}</Field>
      <Field label="Role" hint={ROLE_SUMMARY[role]}>
        {(p) => (
          <select {...p} className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        )}
      </Field>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------

function balanceCaption(meter: string, balance: number, credits: QuotaCredit[]): string {
  const m = meterOf(meter);
  if (meter === "digests") {
    const granted = credits.some((c) => c.meter === "digests");
    if (balance > 0) return `${fmtNum(balance)} left`;
    return granted ? "None left" : "Nothing granted";
  }
  return balance > 0 ? `${m.unit ? `${m.unit} ` : ""}added to the plan` : "Nothing granted";
}

function creditState(c: QuotaCredit): { label: string; tone: string } {
  if (c.revokedAt) return { label: "Revoked", tone: "neutral" };
  if (c.expiresAt && new Date(c.expiresAt).getTime() <= Date.now()) return { label: "Expired", tone: "neutral" };
  if (c.units < 0) return { label: "Reduction", tone: "warn" };
  return { label: "Active", tone: "ok" };
}

function featureGrantState(g: FeatureGrant): { label: string; tone: string } {
  if (g.revokedAt) return { label: "Revoked", tone: "neutral" };
  if (g.expiresAt && new Date(g.expiresAt).getTime() <= Date.now()) return { label: "Expired", tone: "neutral" };
  return { label: "Active", tone: "ok" };
}

export function CreditsTab({ d, onAdd, onReduce, onGrantFeature }: { d: StaffOrgDetail; onAdd: () => void; onReduce: () => void; onGrantFeature: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const [revoking, setRevoking] = useState<QuotaCredit | null>(null);
  const [revokingFeature, setRevokingFeature] = useState<FeatureGrant | null>(null);
  const revoke = useMutation({
    mutationFn: (id: string) => staffApi.revokeCredit(d.orgId, id),
    onSuccess: () => {
      setRevoking(null);
      refresh();
      toast.push("info", "Credit revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const revokeFeature = useMutation({
    mutationFn: (id: string) => staffApi.revokeFeatureGrant(d.orgId, id),
    onSuccess: () => {
      setRevokingFeature(null);
      refresh();
      toast.push("info", "Feature grant revoked.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const anyBalance = METERS.some((m) => (d.balances[m.id] ?? 0) !== 0);
  return (
    <div>
      <div className="cust-section-head">
        <div>
          <h3>Feature grants</h3>
          <p className="muted small">Capabilities added on top of the plan. They are not consumed like credits and can expire or be revoked.</p>
        </div>
        <button type="button" className="btn primary" onClick={onGrantFeature}>Grant feature</button>
      </div>
      <div className="card stack-table cust-ledger">
        <DataTable
          ariaLabel="Feature grants"
          rows={d.featureGrants ?? []}
          rowKey={(g) => g.id}
          empty="No features have been granted."
          rowClassName={(g) => (featureGrantState(g).label === "Active" ? undefined : "cust-row-muted")}
          columns={[
            { key: "feature", header: "Feature", label: "Feature", render: (g) => featureLabel(g.feature) },
            { key: "note", header: "Reason", label: "Reason", render: (g) => g.note },
            { key: "by", header: "By", label: "By", render: (g) => g.createdByEmail || <span className="muted">—</span> },
            {
              key: "state", header: "Status", label: "Status", render: (g) => {
                const s = featureGrantState(g);
                return <span className="cust-plan"><span className={`badge ${s.tone}`}>{s.label}</span>{g.expiresAt && !g.revokedAt ? <span className="muted small">until {formatDate(g.expiresAt)}</span> : null}</span>;
              },
            },
            {
              key: "act", header: "", align: "right", render: (g) => featureGrantState(g).label === "Active" ? (
                <RowMenu label={`Actions for ${featureLabel(g.feature)}`}>
                  <button type="button" role="menuitem" className="danger" onClick={() => setRevokingFeature(g)}>Revoke feature</button>
                </RowMenu>
              ) : null,
            },
          ]}
        />
      </div>

      <div className="cust-section-head">
        <div>
          <h3>Balances</h3>
          <p className="muted small">Credits raise what the plan includes. They apply on any plan and never appear on an invoice. Visual recording minutes cover page canvases and sampled call pictures, and turn those on while any time remains. Live Assist minutes turn Live Assist on while any time remains. Connector call credits turn on the AI assistant connector. Each call spends one credit. Digest credits let a plan that does not include digests send that many. Each digest spends one, and they stop on the expiration date. Extra apps raise how many apps a limited plan includes, and they can expire. After that date, keys for apps past the plan stop. Keys for the apps the plan includes continue.</p>
        </div>
        <div className="cust-actions">
          <button type="button" className="btn" onClick={onReduce} disabled={!anyBalance}>
            Reduce credits
          </button>
          <button type="button" className="btn primary" onClick={onAdd}>
            Add credits
          </button>
        </div>
      </div>
      <div className="cust-stats cust-balances">
        {METERS.map((m) => {
          const b = d.balances[m.id] ?? 0;
          return (
            <div key={m.id} className={`card cust-stat${b > 0 ? " ok" : ""}`}>
              <div className="label">{m.label}</div>
              <div className="value">{m.usd ? fmtUnits(m.id, b, false) : fmtNum(b)}</div>
              <div className="muted small">{balanceCaption(m.id, b, d.credits)}</div>
            </div>
          );
        })}
      </div>

      <div className="cust-section-head" style={{ marginTop: 24 }}>
        <h3>Ledger</h3>
      </div>
      <div className="card stack-table cust-ledger">
        <DataTable
          ariaLabel="Credit ledger"
          rows={d.credits}
          rowKey={(c) => c.id}
          empty="No credits have been granted yet."
          rowClassName={(c) => (c.revokedAt ? "cust-row-muted" : undefined)}
          columns={[
            { key: "when", header: "When", label: "When", className: "nowrap", render: (c) => <span title={formatDateTime(c.createdAt)}>{formatDate(c.createdAt)}</span> },
            { key: "meter", header: "Meter", label: "Meter", render: (c) => meterOf(c.meter).label },
            {
              key: "units",
              header: "Change",
              label: "Change",
              align: "right",
              render: (c) => <span className={`cust-units ${c.units < 0 ? "neg" : "pos"}`}>{fmtUnits(c.meter, c.units)}</span>,
            },
            { key: "note", header: "Reason", label: "Reason", render: (c) => c.note || <span className="muted">—</span> },
            { key: "by", header: "By", label: "By", render: (c) => c.createdByEmail || <span className="muted">—</span> },
            {
              key: "state",
              header: "Status",
              label: "Status",
              render: (c) => {
                const s = creditState(c);
                return (
                  <span className="cust-plan">
                    <span className={`badge ${s.tone}`}>{s.label}</span>
                    {c.revokedAt ? (
                      <span className="muted small">by {c.revokedByEmail || "staff"} · {formatDate(c.revokedAt)}</span>
                    ) : c.expiresAt ? (
                      <span className="muted small">until {formatDate(c.expiresAt)}</span>
                    ) : null}
                  </span>
                );
              },
            },
            {
              key: "act",
              header: "",
              align: "right",
              render: (c) =>
                creditState(c).label === "Active" ? (
                  <RowMenu label={`Actions for credit ${c.id}`}>
                    <button type="button" role="menuitem" className="danger" onClick={() => setRevoking(c)}>
                      Revoke credit
                    </button>
                  </RowMenu>
                ) : null,
            },
          ]}
        />
      </div>

      <ConfirmDialog
        open={!!revoking}
        danger
        title="Revoke credit"
        confirmLabel="Revoke credit"
        busy={revoke.isPending}
        onClose={() => !revoke.isPending && setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      >
        <p style={{ margin: 0 }}>
          The {fmtUnits(revoking?.meter ?? "", revoking?.units ?? 0)} credit on {meterNoun(revoking?.meter ?? "")} stops counting now. It stays in the ledger.
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!revokingFeature}
        danger
        title="Revoke feature"
        confirmLabel="Revoke feature"
        busy={revokeFeature.isPending}
        onClose={() => !revokeFeature.isPending && setRevokingFeature(null)}
        onConfirm={() => revokingFeature && revokeFeature.mutate(revokingFeature.id)}
      >
        <p style={{ margin: 0 }}>{featureLabel(revokingFeature?.feature ?? "")} stops for {d.org.name} now. The grant stays in the history.</p>
      </ConfirmDialog>
    </div>
  );
}

export function FeatureGrantDialog({ d, open, onClose }: { d: StaffOrgDetail; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const active = new Set((d.featureGrants ?? []).filter((g) => featureGrantState(g).label === "Active").map((g) => g.feature));
  const choices = (d.grantable ?? []).filter((f) => !d.planFeatures.includes(f) && !active.has(f));
  const [feature, setFeature] = useState(choices[0] ?? "");
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    setFeature(choices[0] ?? "");
    setExpires("");
    setNote("");
  }, [open, d.grantable, d.planFeatures]);
  const expiresError = expires && new Date(expires).getTime() <= Date.now() ? "Choose a date in the future." : undefined;
  const valid = !!feature && !expiresError && note.trim().length > 0;
  const save = useMutation({
    mutationFn: () => staffApi.addFeatureGrant(d.orgId, {
      feature,
      note: note.trim(),
      expiresAt: expires ? new Date(`${expires}T23:59:59`).toISOString() : undefined,
    }),
    onSuccess: () => {
      refresh();
      toast.push("success", `${featureLabel(feature)} granted.`);
      onClose();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Grant feature"
      subtitle={`Turn on a capability for ${d.org.name} without changing the plan.`}
      width={520}
      className="cust-dialog"
      footer={<><button type="button" className="btn" onClick={onClose} disabled={save.isPending}>Cancel</button><BusyButton className="btn primary" busy={save.isPending} busyLabel="Granting…" disabled={!valid} onClick={() => save.mutate()}>Grant feature</BusyButton></>}
    >
      <Field label="Feature">
        {(p) => <select {...p} className="input" value={feature} onChange={(e) => setFeature(e.target.value)}>{choices.map((f) => <option key={f} value={f}>{featureLabel(f)}</option>)}</select>}
      </Field>
      <Field label="Expires" hint="Leave empty until it is revoked." error={expiresError}>
        {(p) => <input {...p} className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />}
      </Field>
      <Field label="Reason">
        {(p) => <textarea {...p} className="input" rows={3} maxLength={500} placeholder="Pilot through the end of the quarter" value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
    </Dialog>
  );
}

export function CreditDialog({ d, mode, open, onClose }: { d: StaffOrgDetail; mode: "add" | "reduce"; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const [meter, setMeter] = useState<Meter>("stored_sessions");
  const [amount, setAmount] = useState("");
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    const first = mode === "reduce" ? METERS.find((m) => (d.balances[m.id] ?? 0) > 0)?.id : undefined;
    setMeter(first ?? "stored_sessions");
    setAmount("");
    setExpires("");
    setNote("");
  }, [open, mode, d.balances]);

  const n = Number(amount);
  const balance = d.balances[meter] ?? 0;
  const amountError =
    amount === "" ? undefined : !Number.isFinite(n) || n <= 0 ? "Enter an amount above zero." : meter === "digests" && !Number.isInteger(n) ? "Enter a whole number of digests." : meter === "projects" && !Number.isInteger(n) ? "Enter a whole number of apps." : mode === "reduce" && n > balance ? `The balance is ${fmtUnits(meter, balance, false)}.` : undefined;
  const expiresError = expires && new Date(expires).getTime() <= Date.now() ? "Choose a date in the future." : undefined;
  const valid = amount !== "" && !amountError && !expiresError && note.trim().length > 0;

  const save = useMutation({
    mutationFn: () =>
      staffApi.addCredit(d.orgId, {
        meter,
        units: mode === "reduce" ? -n : n,
        note: note.trim(),
        expiresAt: mode === "add" && expires ? new Date(`${expires}T23:59:59`).toISOString() : undefined,
      }),
    onSuccess: () => {
      refresh();
      toast.push("success", mode === "add" ? `Added ${fmtUnits(meter, n)} ${meterNoun(meter)}.` : `Reduced ${meterNoun(meter)} by ${fmtUnits(meter, n, false)}.`);
      onClose();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const m = meterOf(meter);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Add credits" : "Reduce credits"}
      subtitle={mode === "add" ? `Extra included ${meterNoun(meter)} for ${d.org.name}, on any plan.` : `Take back part of what was granted to ${d.org.name}.`}
      width={520}
      className="cust-dialog"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>
            Cancel
          </button>
          <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" disabled={!valid} onClick={() => save.mutate()}>
            {mode === "add" ? "Add credits" : "Reduce credits"}
          </BusyButton>
        </>
      }
    >
      <Field label="Meter" hint={`Current credit balance: ${fmtUnits(meter, balance, false)}`}>
        {(p) => (
          <select {...p} className="input" value={meter} onChange={(e) => setMeter(e.target.value as Meter)}>
            {METERS.filter((x) => mode === "add" || (d.balances[x.id] ?? 0) > 0).map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      <div className="form-grid">
        <Field label={m.usd ? "Amount (USD)" : `Amount${m.unit ? ` (${m.unit})` : ""}`} error={amountError}>
          {(p) => <input {...p} className="input" type="number" inputMode="decimal" min={0} step={m.usd ? 1 : 1} placeholder={m.usd ? "25" : "1000"} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />}
        </Field>
        {mode === "add" && (
          <Field label="Expires" hint="Leave empty and the credit lasts until used or revoked." error={expiresError}>
            {(p) => <input {...p} className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />}
          </Field>
        )}
      </div>
      <Field label="Reason">
        {(p) => <textarea {...p} className="input" rows={3} placeholder={mode === "add" ? "Goodwill after the outage on Sep 12" : "Granted twice by mistake"} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />}
      </Field>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Support access
// ---------------------------------------------------------------------------

const SCOPE_LABELS: Record<string, string> = { read_sessions: "View sessions", read_settings: "View settings", write: "Make changes" };
const GRANT_STATUS: Record<string, { label: string; tone: string }> = {
  pending: { label: "Waiting for customer", tone: "info" },
  active: { label: "Active", tone: "ok" },
  denied: { label: "Denied", tone: "danger" },
  revoked: { label: "Ended", tone: "neutral" },
  expired: { label: "Expired", tone: "neutral" },
};

export function AccessTab({ d, onRequest }: { d: StaffOrgDetail; onRequest: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const [cancelling, setCancelling] = useState<SupportAccessGrant | null>(null);
  const cancel = useMutation({
    mutationFn: (id: string) => staffApi.cancelAccess(d.orgId, id),
    onSuccess: () => {
      setCancelling(null);
      refresh();
      toast.push("info", "Support access ended.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const grants = d.grants.filter((g) => g.status !== "denied" && g.status !== "revoked");
  const openOne = grants.some((g) => g.status === "pending" || g.status === "active");
  return (
    <div>
      <div className="cust-section-head">
        <div>
          <h3>Support access</h3>
          <p className="muted small">Time-boxed access to the customer's sessions and settings. The customer has to accept every request.</p>
        </div>
        <div className="cust-actions">
          <button type="button" className="btn primary" onClick={onRequest} disabled={openOne} title={openOne ? "A request is already open" : undefined}>
            Request access
          </button>
        </div>
      </div>
      <div className="card stack-table cust-access">
        <DataTable
          ariaLabel="Support access requests"
          rows={grants}
          rowKey={(g) => g.id}
          empty="No support access has been requested."
          columns={[
            { key: "status", header: "Status", label: "Status", render: (g) => <span className={`badge ${GRANT_STATUS[g.status]?.tone ?? "neutral"}`}>{GRANT_STATUS[g.status]?.label ?? g.status}</span> },
            { key: "reason", header: "Reason", label: "Reason", render: (g) => g.reason },
            { key: "who", header: "Requested by", label: "Requested by", render: (g) => g.requesterEmail || <span className="muted">—</span> },
            { key: "scopes", header: "Allows", label: "Allows", render: (g) => g.scopes.filter((s) => SCOPE_LABELS[s]).map((s) => SCOPE_LABELS[s]).join(", ") },
            {
              key: "until",
              header: "For",
              label: "For",
              render: (g) => {
                const length = formatAccessLength(g.createdAt, g.expiresAt);
                if (g.status === "pending") return length ? `For ${length}` : formatDateTime(g.expiresAt);
                if (g.status === "active") return `Until ${formatDateTime(g.expiresAt)}`;
                return g.decidedAt ? formatDateTime(g.decidedAt) : formatDateTime(g.expiresAt);
              },
            },
            {
              key: "act",
              header: "",
              align: "right",
              render: (g) =>
                g.status === "pending" || g.status === "active" ? (
                  <RowMenu label={`Actions for request ${g.id}`}>
                    <button type="button" role="menuitem" className="danger" onClick={() => setCancelling(g)}>
                      {g.status === "pending" ? "Withdraw request" : "End access"}
                    </button>
                  </RowMenu>
                ) : null,
            },
          ]}
        />
      </div>
      <ConfirmDialog
        open={!!cancelling}
        danger
        title={cancelling?.status === "pending" ? "Withdraw request" : "End support access"}
        confirmLabel={cancelling?.status === "pending" ? "Withdraw request" : "End access"}
        busy={cancel.isPending}
        onClose={() => !cancel.isPending && setCancelling(null)}
        onConfirm={() => cancelling && cancel.mutate(cancelling.id)}
      >
        <p style={{ margin: 0 }}>
          {cancelling?.status === "pending" ? "The customer will no longer see this request." : `Access to ${d.org.name} ends now.`}
        </p>
      </ConfirmDialog>
    </div>
  );
}

export function AccessDialog({ d, open, onClose }: { d: StaffOrgDetail; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const [reason, setReason] = useState("");
  const [write, setWrite] = useState(false);
  const [hours, setHours] = useState("4");
  useEffect(() => {
    if (open) {
      setReason("");
      setWrite(false);
      setHours("4");
    }
  }, [open]);
  const request = useMutation({
    mutationFn: () =>
      staffApi.requestAccess(d.orgId, {
        reason: reason.trim(),
        scopes: ["read_sessions", "read_settings", ...(write ? ["write"] : [])],
        ttlHours: Number(hours) || 4,
      }),
    onSuccess: () => {
      refresh();
      toast.push("success", "Access requested. The customer has to accept it.");
      onClose();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const h = Number(hours);
  const hoursError = hours !== "" && (!Number.isFinite(h) || h <= 0 || h > 168) ? "Between 1 and 168 hours." : undefined;
  const valid = reason.trim().length > 2 && !hoursError && hours !== "";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Request support access"
      subtitle={`${d.org.name} decides whether to accept. Everything done under the grant is recorded.`}
      width={520}
      className="cust-dialog"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={request.isPending}>
            Cancel
          </button>
          <BusyButton className="btn primary" busy={request.isPending} busyLabel="Sending…" disabled={!valid} onClick={() => request.mutate()}>
            Send request
          </BusyButton>
        </>
      }
    >
      <Field label="Reason" hint="The customer sees this.">
        {(p) => <input {...p} className="input" placeholder="Ticket 4821: replay not loading" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} autoFocus />}
      </Field>
      <Field label="For how long" error={hoursError}>
        {(p) => (
          <select {...p} className="input" value={hours} onChange={(e) => setHours(e.target.value)}>
            <option value="1">1 hour</option>
            <option value="4">4 hours</option>
            <option value="24">1 day</option>
            <option value="72">3 days</option>
            <option value="168">7 days</option>
          </select>
        )}
      </Field>
      <div className="settings-stack" style={{ marginTop: 8 }}>
        <Switch checked={write} onChange={setWrite} label="Make changes" hint="Change settings on the customer's behalf while the grant lasts." />
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

const ACTIVITY_RANGES = [
  { value: "24", label: "Last 24 hours" },
  { value: "168", label: "Last 7 days" },
  { value: "720", label: "Last 30 days" },
  { value: "2160", label: "Last 90 days" },
  { value: "0", label: "All time" },
];

const CUSTOMER_ACTIVITY = ACTIVITY_FAMILIES.filter((f) => !["staff.member", "staff.system_issue.", "staff.ai_feedback.", "billing.", "ai.", "mail."].includes(f.value));

export function ActivityTab({ d }: { d: StaffOrgDetail }) {
  const [qInput, setQInput] = useState("");
  const [family, setFamily] = useState("");
  const [actor, setActor] = useState("");
  const [sinceHours, setSinceHours] = useState(720);
  const q = useDebounced(qInput.trim(), 250);
  const actors = useQuery({ queryKey: staffKeys.activityActors, queryFn: () => staffApi.activityActors(), staleTime: 60_000 });
  const filters = useMemo(
    () => ({ orgId: d.orgId, q: q || undefined, action: family || undefined, actor: actor || undefined, sinceHours }),
    [d.orgId, q, family, actor, sinceHours],
  );
  const filtered = !!(q || family || actor);
  const clear = () => {
    setQInput("");
    setFamily("");
    setActor("");
  };
  return (
    <div>
      <div className="toolbar customers-filters" aria-label="Activity filters">
        <input
          className="input customers-search"
          type="search"
          placeholder="Search people or details"
          aria-label="Search activity"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          autoComplete="off"
        />
        <SearchSelect value={family} options={CUSTOMER_ACTIVITY} onChange={setFamily} aria-label="Kind of action" searchPlaceholder="Search kinds…" />
        <SearchSelect
          value={actor}
          options={[{ value: "", label: "Anyone on the team" }, ...(actors.data?.items ?? []).map((e) => ({ value: e, label: e }))]}
          onChange={setActor}
          aria-label="Who"
          searchPlaceholder="Search people…"
        />
        <SearchSelect value={String(sinceHours)} options={ACTIVITY_RANGES} onChange={(v) => setSinceHours(Number(v))} aria-label="Time range" searchPlaceholder="Search ranges…" />
      </div>
      <div className="card">
        <ActivityFeed
          filters={filters}
          showCustomer={false}
          emptyTitle={filtered ? "Nothing matches" : "No activity yet"}
          emptyHint={
            filtered ? (
              <button type="button" className="btn ghost sm" onClick={clear}>
                Clear filters
              </button>
            ) : (
              "Actions the platform team takes on this customer appear here."
            )
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Networks
// ---------------------------------------------------------------------------

type NetworkDraft = { enabled: boolean; networks: string[]; bypass: string[] };

function networkDraftOf(d: StaffOrgDetail): NetworkDraft {
  const s = d.org.settings;
  return { enabled: !!s.ipAllowlistEnabled, networks: s.ipAllowlist ?? [], bypass: s.ipBypassUserIds ?? [] };
}

/** Platform staff repair a customer's sign-in networks without being on one. */
export function NetworksTab({ d }: { d: StaffOrgDetail }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const saved = useMemo(() => networkDraftOf(d), [d]);
  const [draft, setDraft] = useState<NetworkDraft>(saved);
  const [entry, setEntry] = useState("");
  const [entryError, setEntryError] = useState<string>();
  const [removing, setRemoving] = useState<string | null>(null);
  useEffect(() => setDraft(saved), [saved]);
  const missingNetwork = draft.enabled && draft.networks.length === 0;
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const covered = d.planFeatures.includes("ip_allowlist") || d.planFeatures.includes("everything") || d.features.includes("ip_allowlist");
  const save = useMutation({
    mutationFn: () => staffApi.patchOrg(d.orgId, { settings: { ipAllowlistEnabled: draft.enabled, ipAllowlist: draft.networks, ipBypassUserIds: draft.bypass } }),
    onSuccess: () => {
      refresh();
      toast.push("success", draft.enabled ? "Sign-in networks saved." : "Sign-in is no longer limited by network.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const add = () => {
    const network = normalizeNetwork(entry);
    if (!network) {
      setEntryError("Enter an address or a range, like 203.0.113.10 or 203.0.113.0/24.");
      return;
    }
    setEntry("");
    setEntryError(undefined);
    setDraft((cur) => (cur.networks.includes(network) ? cur : { ...cur, networks: [...cur.networks, network] }));
  };
  return (
    <div className="cust-settings">
      <div className="card form-card">
        <section className="section">
          <h2>Allowed networks</h2>
          <p className="muted small">Change this for the customer when their list locks people out. Your own address does not have to be on it.</p>
          {!covered && <p className="muted small">This plan does not limit sign-in by network until Allowed networks is included or granted. A list saved here is kept and starts applying then.</p>}
          <div className="settings-stack">
            <Switch
              id="staff-limit-networks"
              checked={draft.enabled}
              onChange={(enabled) => setDraft((cur) => ({ ...cur, enabled }))}
              label="Limit sign-in to these networks"
              hint="People and API tokens can connect only from a network on this list. Emergency access accounts can still sign in from anywhere."
            />
            <Field label="Network" error={entryError ?? (missingNetwork ? "Add at least one network." : undefined)} hint="An address, or a range such as 203.0.113.0/24.">
              {(p) => (
                <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    {...p}
                    className="input"
                    value={entry}
                    placeholder="203.0.113.0/24"
                    onChange={(e) => {
                      setEntry(e.target.value);
                      setEntryError(undefined);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        add();
                      }
                    }}
                    style={{ maxWidth: 280 }}
                  />
                  <button type="button" className="btn" disabled={!entry.trim()} onClick={add}>
                    Add network
                  </button>
                </div>
              )}
            </Field>
            {draft.networks.length > 0 && (
              <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {draft.networks.map((network) => (
                  <li key={network} className="row" style={{ justifyContent: "space-between", gap: 12 }}>
                    <span className="mono">{network}</span>
                    <RowMenu label={`Actions for ${network}`}>
                      <button type="button" className="danger" role="menuitem" onClick={() => setRemoving(network)}>
                        Remove network
                      </button>
                    </RowMenu>
                  </li>
                ))}
              </ul>
            )}
            <Field label="Emergency access" hint="These accounts can sign in from anywhere if the list locks people out.">
              {(p) => (
                <select
                  {...p}
                  className="input"
                  multiple
                  value={draft.bypass}
                  onChange={(e) => setDraft((cur) => ({ ...cur, bypass: Array.from(e.target.selectedOptions).map((o) => o.value) }))}
                  style={{ height: 120, maxWidth: 420 }}
                >
                  {d.members.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.email}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        </section>
      </div>
      <SaveBar dirty={dirty} saving={save.isPending} disabled={missingNetwork} onSave={() => save.mutate()} onDiscard={() => { setDraft(saved); setEntry(""); setEntryError(undefined); }} label={d.org.name} />
      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove network"
        confirmLabel="Remove network"
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          setDraft((cur) => ({ ...cur, networks: cur.networks.filter((n) => n !== removing) }));
          setRemoving(null);
        }}
      >
        <p style={{ margin: 0 }}>
          <strong>{removing}</strong> will be removed when you save.
        </p>
      </ConfirmDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

type Draft = {
  name: string;
  settings: Pick<OrgSettings, "canvasDisabled" | "liveDisabled" | "liveNoRemoteControl" | "supportCallingDisabled" | "phi" | "dataRegion" | "sessionIdleMinutes">;
  features: string[];
};

function draftOf(d: StaffOrgDetail): Draft {
  const s = d.org.settings;
  return {
    name: d.org.name,
    settings: {
      canvasDisabled: s.canvasDisabled ?? false,
      liveDisabled: s.liveDisabled ?? false,
      liveNoRemoteControl: s.liveNoRemoteControl ?? false,
      supportCallingDisabled: s.supportCallingDisabled ?? false,
      phi: s.phi,
      dataRegion: s.dataRegion,
      sessionIdleMinutes: s.sessionIdleMinutes,
    },
    features: d.features.filter((f) => (d.grantable ?? []).includes(f)).sort(),
  };
}

const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);

export function SettingsTab({ d }: { d: StaffOrgDetail }) {
  const toast = useToast();
  const refresh = useRefreshCustomer(d.orgId);
  const saved = useMemo(() => draftOf(d), [d]);
  const [draft, setDraft] = useState<Draft>(saved);
  useEffect(() => setDraft(saved), [saved]);
  const dirty = !same(draft, saved);
  const nameError = draft.name.trim() ? undefined : "Enter a name.";
  const idle = draft.settings.sessionIdleMinutes;
  const idleError = !Number.isFinite(idle) || idle < 5 || idle > 1440 ? "Between 5 and 1,440 minutes." : draft.settings.phi && idle > 15 ? "PHI customers sign out after 15 minutes at most." : undefined;
  const valid = !nameError && !idleError;

  const save = useMutation({
    mutationFn: async () => {
      const orgChanged = draft.name !== saved.name || JSON.stringify(draft.settings) !== JSON.stringify(saved.settings);
      const featuresChanged = JSON.stringify(draft.features) !== JSON.stringify(saved.features);
      if (orgChanged) {
        await staffApi.patchOrg(d.orgId, {
          name: draft.name.trim() !== saved.name ? draft.name.trim() : undefined,
          settings: JSON.stringify(draft.settings) !== JSON.stringify(saved.settings) ? draft.settings : undefined,
        });
      }
      if (featuresChanged) await staffApi.setFeatures(d.orgId, { features: draft.features, note: d.featuresNote });
    },
    onSuccess: () => {
      refresh();
      toast.push("success", "Customer settings saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const setS = <K extends keyof Draft["settings"]>(k: K, v: Draft["settings"][K]) => setDraft({ ...draft, settings: { ...draft.settings, [k]: v } });
  const toggleFeature = (f: string, on: boolean) =>
    setDraft({ ...draft, features: (on ? [...draft.features, f] : draft.features.filter((x) => x !== f)).filter((x, i, a) => a.indexOf(x) === i).sort() });
  const grantable = d.grantable ?? [];

  return (
    <div className="cust-settings">
      <div className="card form-card">
        <section className="section">
          <h2>Name</h2>
          <Field label="Organization name" error={nameError}>
            {(p) => <input {...p} className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={120} style={{ maxWidth: 420 }} />}
          </Field>
        </section>

        <section className="section">
          <h2>Recording and Live Assist</h2>
          <p className="muted small">The same switches the customer has. Off means the feature never runs, whatever the plan says.</p>
          <div className="settings-stack">
            <Switch checked={!draft.settings.canvasDisabled} onChange={(v) => setS("canvasDisabled", !v)} label="Canvas recording" hint="Record canvas and WebGL on the page." />
            <Switch checked={!draft.settings.liveDisabled} onChange={(v) => setS("liveDisabled", !v)} label="Live Assist" hint="Connect agents and show a help code." />
            <Switch checked={!draft.settings.liveNoRemoteControl} disabled={draft.settings.liveDisabled} onChange={(v) => setS("liveNoRemoteControl", !v)} label="Remote control" hint="Let an agent control the page during Live Assist." />
            <Switch checked={!draft.settings.supportCallingDisabled} disabled={draft.settings.liveDisabled} onChange={(v) => setS("supportCallingDisabled", !v)} label="Support calls" hint="Let an agent start an audio call during Live Assist." />
          </div>
        </section>

        <section className="section">
          <h2>Compliance</h2>
          <div className="settings-stack">
            <Switch checked={draft.settings.phi} onChange={(v) => setS("phi", v)} label="Handles protected health information" hint="Signs people out after 15 minutes idle, requires strict masking, and keeps AI on providers cleared for health data." />
            <div className="form-grid" style={{ width: "100%" }}>
              <Field label="Sign out after inactivity (minutes)" error={idleError}>
                {(p) => <input {...p} className="input" type="number" min={5} max={1440} value={Number.isFinite(idle) ? idle : ""} onChange={(e) => setS("sessionIdleMinutes", Number(e.target.value))} />}
              </Field>
            </div>
          </div>
        </section>

        <section className="section">
          <h2>Permanent feature grants</h2>
          <p className="muted small">These last until switched off. Use the Grants tab for a grant that expires or needs its own audit history.</p>
          {grantable.length === 0 ? (
            <p className="muted small">This plan already includes every feature that can be added separately.</p>
          ) : (
            <div className="cust-feature-grid">
              {grantable.map((f) => (
                <Switch key={f} checked={draft.features.includes(f)} onChange={(v) => toggleFeature(f, v)} label={featureLabel(f)} />
              ))}
            </div>
          )}
        </section>
      </div>
      <SaveBar dirty={dirty} saving={save.isPending} disabled={!valid} onSave={() => save.mutate()} onDiscard={() => setDraft(saved)} label={d.org.name} />
    </div>
  );
}

function estimatedMonthlyCost(expectedSessions: number, aiCredits: number, expectedCustomEvents = 0) {
  return Math.max(0, expectedSessions) * 0.004 + Math.max(0, expectedCustomEvents) * 0.4 / 1_000_000 + Math.max(0, aiCredits) / 2;
}

export function enterpriseQuote(expectedSessions: number, aiCredits: number, targetMarginPct: number, expectedCustomEvents = 0) {
  const estimatedCostUsd = estimatedMonthlyCost(expectedSessions, aiCredits, expectedCustomEvents);
  const margin = 1 - targetMarginPct / 100;
  // Round to the cent before stepping up to the next $100, so a price that
  // lands on a hundred (1500 / 0.2 = 7500) is not pushed up by float noise.
  const raw = Math.round((estimatedCostUsd / (margin > 0 ? margin : 0.3)) * 100) / 100;
  const recommendedMonthlyUsd = Math.ceil(Math.max(5000, raw) / 100) * 100;
  return { estimatedCostUsd, recommendedMonthlyUsd, annualCommitUsd: recommendedMonthlyUsd * 12 };
}

/** What the agreement earns at the prices currently on the form. */
export function enterpriseOutlook(input: {
  expectedSessions: number;
  sessionsIncluded: number;
  aiCredits: number;
  monthlyPlatformUsd: number;
  sessionOveragePer1000: number;
  expectedCustomEvents?: number;
  customEventsIncluded?: number;
  customEventOveragePerMillion?: number;
}) {
  const events = Math.max(0, input.expectedCustomEvents ?? 0);
  const estimatedCostUsd = estimatedMonthlyCost(input.expectedSessions, input.aiCredits, events);
  const extraSessions = Math.max(0, input.expectedSessions - Math.max(0, input.sessionsIncluded));
  const extraSessionRevenue = (extraSessions / 1000) * Math.max(0, input.sessionOveragePer1000);
  const extraEvents = Math.max(0, events - Math.max(0, input.customEventsIncluded ?? 0));
  const extraEventRevenue = (extraEvents / 1_000_000) * Math.max(0, input.customEventOveragePerMillion ?? 0);
  const monthlyRevenue = Math.max(0, input.monthlyPlatformUsd) + extraSessionRevenue + extraEventRevenue;
  const monthlyProfit = monthlyRevenue - estimatedCostUsd;
  const grossMarginPct = monthlyRevenue > 0 ? (monthlyProfit / monthlyRevenue) * 100 : 0;
  return { estimatedCostUsd, extraSessionRevenue, extraEventRevenue, monthlyRevenue, monthlyProfit, grossMarginPct, annualProfit: monthlyProfit * 12 };
}

function fmtPct(n: number) {
  const tenths = Math.round(n * 10) / 10;
  return `${tenths.toFixed(Number.isInteger(tenths) ? 0 : 1)}%`;
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

export function EnterpriseOfferForm({ d }: { d: StaffOrgDetail }) {
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshCustomer(d.orgId);
  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setUTCFullYear(nextYear.getUTCFullYear() + 1);
  const defaults = {
    recipientUserId: d.members.find((m) => m.role === "owner")?.userId ?? d.members.find((m) => m.role === "admin")?.userId ?? "",
    contactEmail: d.org.profile?.contactEmail ?? d.customer.contactEmail ?? "",
    periodStart: isoDate(today),
    periodEnd: isoDate(nextYear),
    expectedSessionsMonthly: "250000",
    sessionsIncluded: "250000",
    canvasMinutesIncluded: "100000",
    liveMinutesIncluded: "50000",
    aiCreditsIncludedUsd: "1000",
    targetMarginPct: "70",
    sessionOveragePer1000: "12",
    canvasMinuteOverage: "0.01",
    liveMinuteOverage: "0.03",
    expectedCustomEventsMonthly: "0",
    customEventsIncluded: "50000000",
    customEventOveragePerMillion: "2",
    monthlyPlatformUsd: "5000",
    annualCommitUsd: "60000",
    note: "",
  };
  const [form, setForm] = useState(defaults);
  const number = (key: keyof typeof form) => Number(form[key]) || 0;
  const quote = enterpriseQuote(number("expectedSessionsMonthly"), number("aiCreditsIncludedUsd"), number("targetMarginPct"), number("expectedCustomEventsMonthly"));
  const outlook = enterpriseOutlook({
    expectedSessions: number("expectedSessionsMonthly"),
    sessionsIncluded: number("sessionsIncluded"),
    aiCredits: number("aiCreditsIncludedUsd"),
    monthlyPlatformUsd: number("monthlyPlatformUsd"),
    sessionOveragePer1000: number("sessionOveragePer1000"),
    expectedCustomEvents: number("expectedCustomEventsMonthly"),
    customEventsIncluded: number("customEventsIncluded"),
    customEventOveragePerMillion: number("customEventOveragePerMillion"),
  });
  const belowRecommendation = number("monthlyPlatformUsd") < quote.recommendedMonthlyUsd;
  const annualTooLow = number("annualCommitUsd") < number("monthlyPlatformUsd") * 12;
  const emailError = form.contactEmail && !/.+@.+\..+/.test(form.contactEmail) ? "Enter a valid email." : undefined;
  const valid =
    !!form.recipientUserId &&
    !!form.contactEmail &&
    !emailError &&
    number("targetMarginPct") >= 0 &&
    number("targetMarginPct") < 100 &&
    number("sessionsIncluded") > 0 &&
    number("sessionOveragePer1000") > 0 &&
    number("canvasMinuteOverage") > 0 &&
    number("liveMinuteOverage") > 0 &&
    number("customEventsIncluded") > 0 &&
    number("customEventOveragePerMillion") > 0 &&
    number("monthlyPlatformUsd") > 0 &&
    !annualTooLow &&
    (!belowRecommendation || !!form.note.trim());
  const set = (key: keyof typeof form, value: string) =>
    setForm((x) => {
      const next = { ...x, [key]: value };
      const num = (k: keyof typeof next) => Number(next[k]) || 0;
      // Included sessions start out equal to the forecast. Keep them together
      // until someone sets a different included amount on purpose.
      if (key === "expectedSessionsMonthly" && x.sessionsIncluded === x.expectedSessionsMonthly) next.sessionsIncluded = value;
      if (key === "expectedSessionsMonthly" || key === "aiCreditsIncludedUsd" || key === "targetMarginPct" || key === "expectedCustomEventsMonthly") {
        const q = enterpriseQuote(num("expectedSessionsMonthly"), num("aiCreditsIncludedUsd"), num("targetMarginPct"), num("expectedCustomEventsMonthly"));
        next.monthlyPlatformUsd = String(q.recommendedMonthlyUsd);
        next.annualCommitUsd = String(q.annualCommitUsd);
      } else if (key === "monthlyPlatformUsd") {
        next.annualCommitUsd = String(num("monthlyPlatformUsd") * 12);
      }
      return next;
    });
  const useRecommendation = () =>
    setForm((x) => ({ ...x, monthlyPlatformUsd: String(quote.recommendedMonthlyUsd), annualCommitUsd: String(quote.annualCommitUsd) }));
  const activate = useMutation({
    mutationFn: () => {
      const body: EnterpriseContractInput = {
        recipientUserId: form.recipientUserId,
        contactEmail: form.contactEmail.trim(),
        periodStart: new Date(`${form.periodStart}T00:00:00Z`).toISOString(),
        periodEnd: new Date(`${form.periodEnd}T00:00:00Z`).toISOString(),
        expectedSessionsMonthly: number("expectedSessionsMonthly"),
        sessionsIncluded: number("sessionsIncluded"),
        canvasMinutesIncluded: number("canvasMinutesIncluded"),
        liveMinutesIncluded: number("liveMinutesIncluded"),
        aiCreditsIncludedUsd: number("aiCreditsIncludedUsd"),
        targetMarginPct: number("targetMarginPct"),
        sessionOveragePer1000: number("sessionOveragePer1000"),
        canvasMinuteOverage: number("canvasMinuteOverage"),
        liveMinuteOverage: number("liveMinuteOverage"),
        expectedCustomEventsMonthly: number("expectedCustomEventsMonthly"),
        customEventsIncluded: number("customEventsIncluded"),
        customEventOveragePerMillion: number("customEventOveragePerMillion"),
        monthlyPlatformUsd: number("monthlyPlatformUsd"),
        annualCommitUsd: number("annualCommitUsd"),
        note: form.note.trim(),
      };
      return staffApi.sendEnterpriseOffer(d.orgId, body);
    },
    onSuccess: () => {
      refresh();
      toast.push("success", `Enterprise offer sent for ${d.org.name}.`);
      void navigate({ to: "/staff/orgs/$org", params: { org: d.orgId }, search: { tab: "billing" } });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  return (
    <div className="card form-card cust-enterprise-form">
      <div className="form-grid">
        <Field className="span-2" label="Send offer to">
          {(p) => (
            <select {...p} className="input" value={form.recipientUserId} onChange={(e) => set("recipientUserId", e.target.value)} autoFocus>
              {d.members.filter((m) => m.role === "owner" || m.role === "admin").map((m) => (
                <option key={m.userId} value={m.userId}>{m.name ? `${m.name} · ${m.email}` : m.email} · {m.role}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Billing email" error={emailError}>
          {(p) => <input {...p} className="input" type="email" value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} />}
        </Field>
        <Field label="Target gross margin" hint="Percent">
          {(p) => <input {...p} className="input" type="number" min={0} max={99} step={1} value={form.targetMarginPct} onChange={(e) => set("targetMarginPct", e.target.value)} />}
        </Field>
        <Field label="Starts">
          {(p) => <input {...p} className="input" type="date" value={form.periodStart} onChange={(e) => set("periodStart", e.target.value)} />}
        </Field>
        <Field label="Ends">
          {(p) => <input {...p} className="input" type="date" value={form.periodEnd} onChange={(e) => set("periodEnd", e.target.value)} />}
        </Field>
      </div>

      <h3>Expected monthly usage</h3>
      <div className="form-grid">
        <Field label="Stored sessions">
          {(p) => <input {...p} className="input" type="number" min={0} step={1000} value={form.expectedSessionsMonthly} onChange={(e) => set("expectedSessionsMonthly", e.target.value)} />}
        </Field>
        <Field label="AI credits included" hint="Dollars">
          {(p) => <input {...p} className="input" type="number" min={0} step={100} value={form.aiCreditsIncludedUsd} onChange={(e) => set("aiCreditsIncludedUsd", e.target.value)} />}
        </Field>
        <Field label="Custom events" hint="Forecast for the cost estimate. The included amount is set below.">
          {(p) => <input {...p} className="input" type="number" min={0} step={1000000} value={form.expectedCustomEventsMonthly} onChange={(e) => set("expectedCustomEventsMonthly", e.target.value)} />}
        </Field>
      </div>
      <div className="cust-quote">
        <div className="cust-section-head">
          <div>
            <h3>Monthly result</h3>
            <p className="muted small">0.4¢ per stored session, $0.40 per million custom events, plus half of the AI credits. Usage and target margin update the monthly fee and the annual commitment.</p>
          </div>
          {number("monthlyPlatformUsd") !== quote.recommendedMonthlyUsd && (
            <button type="button" className="btn" onClick={useRecommendation}>Use recommendation</button>
          )}
        </div>
        <div className="cust-stats cust-quote-stats">
          <Tile label="Estimated monthly cost" value={fmtUsd(outlook.estimatedCostUsd)} />
          <Tile
            label="Monthly gross profit"
            value={fmtUsd(outlook.monthlyProfit)}
            tone={outlook.monthlyProfit < 0 ? "warn" : undefined}
            hint={`${fmtUsd(outlook.annualProfit)} over the year`}
          />
          <Tile
            label="Gross margin"
            value={fmtPct(outlook.grossMarginPct)}
            tone={outlook.grossMarginPct + 0.05 < number("targetMarginPct") ? "warn" : undefined}
            hint={`Target ${fmtPct(number("targetMarginPct"))}`}
          />
        </div>
        {outlook.extraSessionRevenue > 0 && (
          <p className="muted small">Includes {fmtUsd(outlook.extraSessionRevenue)} this month from sessions past the included amount.</p>
        )}
        {outlook.extraEventRevenue > 0 && (
          <p className="muted small">Includes {fmtUsd(outlook.extraEventRevenue)} this month from custom events past the included amount.</p>
        )}
      </div>

      <h3>Agreement pricing</h3>
      <div className="form-grid">
        <Field label="Monthly platform fee">
          {(p) => <input {...p} className="input" type="number" min={1} step={100} value={form.monthlyPlatformUsd} onChange={(e) => set("monthlyPlatformUsd", e.target.value)} />}
        </Field>
        <Field label="Annual commitment" error={annualTooLow ? `At least ${fmtUsd(number("monthlyPlatformUsd") * 12)}.` : undefined}>
          {(p) => <input {...p} className="input" type="number" min={1} step={1000} value={form.annualCommitUsd} onChange={(e) => set("annualCommitUsd", e.target.value)} />}
        </Field>
      </div>

      <h3>Included each month and extra usage</h3>
      <div className="form-grid">
        <Field label="Sessions included">
          {(p) => <input {...p} className="input" type="number" min={1} step={1000} value={form.sessionsIncluded} onChange={(e) => set("sessionsIncluded", e.target.value)} />}
        </Field>
        <Field label="Per 1,000 extra sessions">
          {(p) => <input {...p} className="input" type="number" min={0.01} step={0.5} value={form.sessionOveragePer1000} onChange={(e) => set("sessionOveragePer1000", e.target.value)} />}
        </Field>
        <Field label="Visual recording minutes included">
          {(p) => <input {...p} className="input" type="number" min={0} step={1000} value={form.canvasMinutesIncluded} onChange={(e) => set("canvasMinutesIncluded", e.target.value)} />}
        </Field>
        <Field label="Per extra visual minute">
          {(p) => <input {...p} className="input" type="number" min={0.0001} step={0.001} value={form.canvasMinuteOverage} onChange={(e) => set("canvasMinuteOverage", e.target.value)} />}
        </Field>
        <Field label="Live Assist minutes included">
          {(p) => <input {...p} className="input" type="number" min={0} step={1000} value={form.liveMinutesIncluded} onChange={(e) => set("liveMinutesIncluded", e.target.value)} />}
        </Field>
        <Field label="Per extra Live Assist minute">
          {(p) => <input {...p} className="input" type="number" min={0.0001} step={0.005} value={form.liveMinuteOverage} onChange={(e) => set("liveMinuteOverage", e.target.value)} />}
        </Field>
        <Field label="Custom events included">
          {(p) => <input {...p} className="input" type="number" min={1} step={1000000} value={form.customEventsIncluded} onChange={(e) => set("customEventsIncluded", e.target.value)} />}
        </Field>
        <Field label="Per extra million custom events" hint="What the customer pays. Cost used above is $0.40 per million.">
          {(p) => <input {...p} className="input" type="number" min={0.01} step={0.5} value={form.customEventOveragePerMillion} onChange={(e) => set("customEventOveragePerMillion", e.target.value)} />}
        </Field>
      </div>
      <Field
        label="Internal note"
        hint={belowRecommendation ? "Required because the monthly fee is below the recommendation." : "Record negotiation details or approvals."}
        error={belowRecommendation && !form.note.trim() ? "Explain the pricing exception." : undefined}
      >
        {(p) => <textarea {...p} className="input" rows={3} value={form.note} onChange={(e) => set("note", e.target.value)} maxLength={1000} />}
      </Field>
      <div className="banner info" role="note">The recipient reviews this offer in Backstory. Accepting creates a Stripe invoice for the annual commitment, due in 7 days. The plan changes only after Stripe confirms payment.</div>
      <div className="cust-actions" style={{ justifyContent: "flex-end", marginTop: 20 }}>
        <Link className="btn" to="/staff/orgs/$org" params={{ org: d.orgId }} search={{ tab: "billing" }}>Cancel</Link>
        <BusyButton className="btn primary" busy={activate.isPending} busyLabel="Sending…" disabled={!valid} onClick={() => activate.mutate()}>
          Send Enterprise offer
        </BusyButton>
      </div>
    </div>
  );
}